import React, { useState, useEffect, useCallback } from 'react';
import { userService } from '../services/api';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from './ui';

const WeeklyCompletionGrid = ({ userId }) => {
  const [weekStart, setWeekStart] = useState(() => {
    // Start with Sunday of current week
    const now = new Date();
    const day = now.getDay();
    const diff = now.getDate() - day;
    return new Date(now.setDate(diff));
  });
  const [gridData, setGridData] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);
  const [expandedActivity, setExpandedActivity] = useState(null);

  const loadWeeklyStats = useCallback(async () => {
    setLoading(true);
    setMessage(null);

    try {
      const weekStartStr = weekStart.toISOString().split('T')[0];
      const response = await userService.getWeeklyActivityStats(weekStartStr);

      setGridData(response.data.data.grid || {});
      setStats(response.data.data.stats || {});
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to load activity stats'
      });
    } finally {
      setLoading(false);
    }
  }, [weekStart]);

  useEffect(() => {
    loadWeeklyStats();
  }, [loadWeeklyStats]);

  const getDayHeader = (offset) => {
    const date = new Date(weekStart);
    date.setDate(date.getDate() + offset);
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    return {
      day: dayNames[date.getDay()],
      date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      key: date.toISOString().split('T')[0]
    };
  };

  const getCompletionColor = (completed) => {
    if (!completed) return 'bg-neutral-100 border-neutral-200';
    if (completed.difficulty === 'very_easy' || completed.difficulty === 'easy') {
      return 'bg-success-200 border-success-300';
    }
    if (completed.difficulty === 'very_difficult') {
      return 'bg-warning-200 border-warning-300';
    }
    return 'bg-primary-200 border-primary-300';
  };

  const previousWeek = () => {
    setWeekStart(new Date(weekStart.getTime() - 7 * 24 * 60 * 60 * 1000));
  };

  const nextWeek = () => {
    setWeekStart(new Date(weekStart.getTime() + 7 * 24 * 60 * 60 * 1000));
  };

  const isCurrentWeek = () => {
    const now = new Date();
    const diff = Math.abs(now.getTime() - weekStart.getTime());
    return diff < 7 * 24 * 60 * 60 * 1000;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-neutral-600">Loading activity grid...</div>
      </div>
    );
  }

  const days = Array.from({ length: 7 }, (_, i) => getDayHeader(i));
  const activities = Object.values(gridData).map(a => a.activity);

  return (
    <div className="space-y-6">
      {message && (
        <div
          className={`rounded-soft border p-3 text-sm ${
            message.type === 'error'
              ? 'border-error-200 bg-error-50 text-error-700'
              : 'border-success-200 bg-success-50 text-success-700'
          }`}
        >
          {message.text}
        </div>
      )}

      {/* Week Navigation */}
      <div className="flex items-center justify-between">
        <Button
          type="button"
          variant="secondary"
          onClick={previousWeek}
          size="sm"
        >
          <ChevronLeft className="w-4 h-4" />
        </Button>

        <div className="text-center">
          <h3 className="font-semibold text-neutral-900">
            Week of {weekStart.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
          </h3>
          {isCurrentWeek() && (
            <p className="text-xs text-primary-600 mt-1">This Week</p>
          )}
        </div>

        <Button
          type="button"
          variant="secondary"
          onClick={nextWeek}
          size="sm"
          disabled={isCurrentWeek()}
        >
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>

      {/* Stats Summary */}
      {stats && (
        <div className="grid gap-4 sm:grid-cols-3">
          <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-3">
            <p className="text-xs font-medium text-neutral-600">Total Completions</p>
            <p className="text-2xl font-bold text-neutral-900 mt-1">{stats.totalCompletions || 0}</p>
          </div>
          <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-3">
            <p className="text-xs font-medium text-neutral-600">Activities Tracked</p>
            <p className="text-2xl font-bold text-neutral-900 mt-1">{stats.totalActivities || 0}</p>
          </div>
          <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-3">
            <p className="text-xs font-medium text-neutral-600">Days Active</p>
            <p className="text-2xl font-bold text-neutral-900 mt-1">
              {Object.values(stats.completionsByDay || {}).filter(c => c > 0).length}
            </p>
          </div>
        </div>
      )}

      {/* Grid */}
      {activities.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr>
                <th className="text-left p-2 border border-neutral-200 bg-neutral-50 font-semibold text-sm text-neutral-700 min-w-40">
                  Activity
                </th>
                {days.map(day => (
                  <th key={day.key} className="p-2 border border-neutral-200 bg-neutral-50 font-semibold text-xs text-neutral-700 text-center min-w-16">
                    <div>{day.day}</div>
                    <div className="text-xs font-normal text-neutral-500 mt-0.5">{day.date}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Object.entries(gridData).map(([activityId, activityData]) => (
                <tr key={activityId} className="border-b border-neutral-200">
                  <td className="p-2 border border-neutral-200 bg-neutral-50">
                    <button
                      onClick={() => setExpandedActivity(expandedActivity === activityId ? null : activityId)}
                      className="text-left w-full hover:text-primary-600 transition-colors"
                    >
                      <p className="font-medium text-sm text-neutral-900">{activityData.activity.name}</p>
                      <p className="text-xs text-neutral-500">{activityData.activity.category.replace(/_/g, ' ')}</p>
                    </button>
                  </td>
                  {days.map(day => {
                    const completion = activityData.days[day.key];
                    return (
                      <td key={day.key} className="p-1 border border-neutral-200 text-center">
                        {completion ? (
                          <div className={`rounded border-2 p-2 cursor-pointer transition-all ${getCompletionColor(completion)}`}
                            onClick={() => setExpandedActivity(expandedActivity === `${activityId}-${day.key}` ? null : `${activityId}-${day.key}`)}>
                            <div className="text-xs font-semibold text-neutral-700">✓</div>
                            <div className="text-xs text-neutral-600">{completion.duration}m</div>
                          </div>
                        ) : (
                          <div className="rounded border-2 border-dashed border-neutral-200 bg-neutral-50 p-2 h-12 flex items-center justify-center">
                            <span className="text-xs text-neutral-300">-</span>
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center py-8 rounded-lg border border-dashed border-neutral-300 bg-neutral-50">
          <p className="text-neutral-600">No activities logged this week yet.</p>
          <p className="text-sm text-neutral-500 mt-1">Start logging activities to see your progress grid.</p>
        </div>
      )}

      {/* Legend */}
      <div className="flex items-center gap-4 text-xs text-neutral-600 pt-4 border-t border-neutral-200">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-success-200 border-2 border-success-300"></div>
          <span>Easy/Very Easy</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-primary-200 border-2 border-primary-300"></div>
          <span>Moderate</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-warning-200 border-2 border-warning-300"></div>
          <span>Very Difficult</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-neutral-100 border-2 border-neutral-200"></div>
          <span>Not Completed</span>
        </div>
      </div>
    </div>
  );
};

export default WeeklyCompletionGrid;
